import { useEffect, useState, useCallback } from "react";
import { Loader2, UserRound, Lock, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { AdminShell } from "./AdminShell";
import { PageHeader } from "./parts";

export default function AdminStudents() {
  const [students, setStudents] = useState(null);
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("all");
  const [query, setQuery] = useState("");

  useEffect(() => { api.get("/admin/schools").then((r) => setSchools(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setStudents(null);
    try {
      const url = schoolId === "all" ? "/admin/students" : `/admin/students?school_id=${schoolId}`;
      const res = await api.get(url);
      setStudents(res.data);
    } catch { toast.error("Impossible de charger les élèves."); }
  }, [schoolId]);
  useEffect(() => { load(); }, [load]);

  const filtered = (students || []).filter((s) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return `${s.first_name} ${s.last_name} ${s.class_name || ""}`.toLowerCase().includes(q);
  });

  return (
    <AdminShell>
      <PageHeader title="Élèves" subtitle="Consultation en lecture seule." />

      <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="privacy-notice">
        <Lock size={16} className="shrink-0" />
        Confidentialité : aucune donnée pédagogique (commentaires, couleurs, notes des passeports) n'est accessible depuis l'administration.
      </div>

      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Rechercher un élève ou une classe…" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9" data-testid="student-search" />
        </div>
        <Select value={schoolId} onValueChange={setSchoolId}>
          <SelectTrigger className="sm:w-64" data-testid="student-school-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les écoles</SelectItem>
            {schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {students === null ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-slate-400" size={28} /></div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid="students-table">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3 text-xs text-slate-500">
            <span>{filtered.length} élève{filtered.length > 1 ? "s" : ""}</span>
          </div>
          <table className="w-full text-sm">
            <thead className="border-b border-slate-100 bg-slate-50/50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="px-5 py-3">Élève</th><th className="px-5 py-3">Classe</th></tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/40" data-testid={`student-row-${s.id}`}>
                  <td className="px-5 py-3.5 font-medium text-slate-900"><div className="flex items-center gap-2"><UserRound size={15} className="text-slate-400" />{s.first_name} {s.last_name}</div></td>
                  <td className="px-5 py-3.5 text-slate-600">{s.class_name || "—"}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={2} className="px-5 py-16 text-center text-sm text-slate-400">Aucun élève.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </AdminShell>
  );
}
